import { useState } from "react";

import type { QueueJob, QueueJobDiagnostics, QueueJobStatus } from "@/lib/api";
import { formatDateTime, formatDateTimeSec, formatDuration } from "@/lib/format";

const STATUS_CLS: Record<QueueJobStatus, string> = {
  queued: "border-amber-800 bg-amber-900/40 text-amber-300",
  dispatched: "border-sky-800 bg-sky-900/40 text-sky-300",
  preempting: "border-fuchsia-800 bg-fuchsia-900/40 text-fuchsia-300",
  preempted: "border-violet-800 bg-violet-900/40 text-violet-300",
  started: "border-blue-800 bg-blue-900/40 text-blue-300",
  progress: "border-blue-700 bg-blue-900/50 text-blue-200",
  retry: "border-orange-800 bg-orange-900/40 text-orange-300",
  success: "border-emerald-800 bg-emerald-900/40 text-emerald-300",
  failure: "border-red-800 bg-red-900/40 text-red-300",
  canceled: "border-ink-600 bg-ink-800 text-ink-400",
};

const STATUS_LABEL: Record<QueueJobStatus, string> = {
  queued: "Na fila",
  dispatched: "Despachado",
  preempting: "Pausando",
  preempted: "Retomado",
  started: "Iniciado",
  progress: "Em progresso",
  retry: "Retry",
  success: "Sucesso",
  failure: "Falha",
  canceled: "Cancelado",
};

const PRIORITIES = [
  { value: 0, label: "Background" },
  { value: 30, label: "Normal" },
  { value: 70, label: "Alta" },
  { value: 100, label: "Urgente" },
];

const TYPE_LABEL: Record<string, string> = {
  "viewer.render": "Render v1",
  "viewer.preprepare": "Preprepare",
  "viewer.rehydrate": "Recuperação",
  "viewer.layers": "Camadas",
  "render.preview": "Render",
  barcode: "Barcode",
};

function priorityLabel(priority: number): string {
  const exact = PRIORITIES.find((item) => item.value === priority);
  if (exact) return exact.label;
  if (priority >= 100) return "Urgente";
  if (priority >= 70) return "Alta";
  if (priority <= 0) return "Background";
  return "Normal";
}

function operationalMessage(job: QueueJob): string | null {
  if (job.operational_note) {
    return job.operational_note;
  }
  if (job.status === "preempting") {
    return "Pausa por prioridade solicitada. O worker vai parar no próximo checkpoint seguro.";
  }
  if (job.status === "preempted") {
    return "Interrompido por prioridade. O trabalho seguiu num novo job de retomada, colocado na fila automaticamente.";
  }
  if (job.status === "dispatched") {
    return "Despachado para o worker e aguardando início da execução.";
  }
  return null;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-ink-500">{label}</span>
      <span className="break-all text-xs text-ink-200">{value}</span>
    </div>
  );
}

function jobDuration(job: QueueJob): string {
  if (!job.started_at) return "—";
  const endTs = job.finished_at ? new Date(job.finished_at).getTime() : Date.now();
  const secs = (endTs - new Date(job.started_at).getTime()) / 1000;
  if (secs < 0) return "—";
  return formatDuration(secs);
}

function diagnosticsStateLabel(state: string): string {
  if (state === "active") return "Ativa";
  if (state === "reserved") return "Reservada";
  if (state === "scheduled") return "Agendada";
  if (state === "not_found") return "Não encontrada";
  if (state === "missing_task_id") return "Sem task ID";
  return state;
}

interface Props {
  job: QueueJob;
  diagnostics: QueueJobDiagnostics | null;
  diagnosticsLoading: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onRetry: (id: string) => Promise<void>;
  onCancel: (id: string) => Promise<void>;
  onFetchDiagnostics: (id: string) => Promise<void>;
}

export function QueueJobDrawer({
  job,
  diagnostics,
  diagnosticsLoading,
  isAdmin,
  onClose,
  onRetry,
  onCancel,
  onFetchDiagnostics,
}: Props) {
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const message = operationalMessage(job);
  // Jobs do Viewer também aceitam retry: o backend cria um job novo na fila
  // (a falha permanece como registro histórico) e o ciclo de tentativas
  // automáticas recomeça.
  const canRetry = job.status === "failure" || job.status === "canceled";
  const canCancel =
    job.status === "queued" ||
    job.status === "dispatched" ||
    job.status === "started" ||
    job.status === "progress" ||
    job.status === "retry" ||
    job.status === "preempting" ||
    job.status === "preempted";

  async function handleAction(fn: () => Promise<void>) {
    setActionLoading(true);
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Erro ao executar ação.");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <aside className="flex w-80 shrink-0 flex-col overflow-hidden border-l border-ink-700 bg-ink-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-ink-700 px-4 py-3">
        <span className="text-sm font-medium text-ink-100">Detalhe do Job</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar painel"
          className="rounded p-1 text-ink-400 hover:text-ink-100"
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
        {message && (
          <div className="rounded border border-sky-800 bg-sky-950/30 px-3 py-2 text-xs leading-relaxed text-sky-200">
            {message}
          </div>
        )}

        <Row label="ID" value={<span className="font-mono">{job.id}</span>} />
        <Row
          label="Status"
          value={
            <span className={`rounded border px-1.5 py-0.5 text-xs ${STATUS_CLS[job.status]}`}>
              {STATUS_LABEL[job.status] ?? job.status}
            </span>
          }
        />
        <Row label="Tipo" value={TYPE_LABEL[job.type] ?? job.type} />
        <Row label="Prioridade" value={`${priorityLabel(job.priority)} (${job.priority})`} />
        <Row label="Progresso" value={`${job.progress}%${job.progress_message ? ` — ${job.progress_message}` : ""}`} />
        <Row label="Tentativa" value={`${Math.max(1, job.attempts)}ª`} />
        <Row label="Preempções" value={String(job.preempt_count)} />
        {job.file_name && <Row label="Arquivo" value={job.file_name} />}
        {job.file_path && (
          <Row label="Caminho" value={<span className="break-all font-mono text-[11px]">{job.file_path}</span>} />
        )}
        {job.file_id && (
          <Row label="Arquivo (ID)" value={<span className="font-mono">{job.file_id}</span>} />
        )}
        {job.render_id && (
          <Row label="Render (ID)" value={<span className="font-mono">{job.render_id}</span>} />
        )}
        {job.celery_task_id && (
          <Row label="Celery Task ID" value={<span className="font-mono text-[10px]">{job.celery_task_id}</span>} />
        )}
        <Row label="Heartbeat" value={formatDateTimeSec(job.heartbeat_at)} />
        <Row label="Enfileirado" value={formatDateTime(job.queued_at)} />
        <Row label="Despachado" value={formatDateTime(job.dispatched_at)} />
        <Row label="Iniciado" value={formatDateTime(job.started_at)} />
        <Row label="Finalizado" value={formatDateTime(job.finished_at)} />
        <Row label="Duração" value={jobDuration(job)} />

        {(job.preempt_requested_at || job.preempted_at || job.preempted_by_job_id) && (
          <div className="flex flex-col gap-2 rounded border border-ink-700 bg-ink-850 p-3">
            <span className="text-[10px] font-medium uppercase tracking-wide text-ink-500">
              Preempção
            </span>
            {job.preempt_requested_at && (
              <Row label="Solicitada" value={formatDateTimeSec(job.preempt_requested_at)} />
            )}
            {job.preempted_at && (
              <Row label="Interrompido em" value={formatDateTimeSec(job.preempted_at)} />
            )}
            {job.preempted_by_job_id && (
              <Row
                label="Causada por"
                value={<span className="font-mono">{job.preempted_by_job_id}</span>}
              />
            )}
          </div>
        )}

        {job.error && (
          <div className="flex flex-col gap-1 rounded border border-red-800 bg-red-950/40 p-3">
            <span className="text-[10px] font-medium uppercase tracking-wide text-red-400">
              Erro técnico
            </span>
            <pre className="whitespace-pre-wrap break-all text-[11px] text-red-300">
              {job.error}
            </pre>
          </div>
        )}

        <div className="flex flex-col gap-3 rounded border border-ink-700 bg-ink-850 p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] font-medium uppercase tracking-wide text-ink-500">
              Diagnóstico operacional
            </span>
            <button
              type="button"
              disabled={diagnosticsLoading}
              onClick={() => void onFetchDiagnostics(job.id)}
              className="rounded border border-ink-700 bg-ink-800 px-2 py-1 text-[11px] text-ink-300 hover:border-ink-500 hover:text-ink-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {diagnosticsLoading ? "Verificando…" : "Verificar agora"}
            </button>
          </div>
          {diagnostics ? (
            <div className="flex flex-col gap-2">
              <Row label="Celery" value={diagnosticsStateLabel(diagnostics.celery_state)} />
              {diagnostics.worker_hostname && (
                <Row label="Worker" value={diagnostics.worker_hostname} />
              )}
              {diagnostics.queue && <Row label="Queue" value={diagnostics.queue} />}
              {diagnostics.active_seconds !== null && (
                <Row label="Tempo ativo" value={formatDuration(diagnostics.active_seconds)} />
              )}
              {diagnostics.heartbeat_age_seconds !== null && (
                <Row label="Idade do heartbeat" value={formatDuration(diagnostics.heartbeat_age_seconds)} />
              )}
              <p className="rounded border border-ink-700 bg-ink-900/60 px-2 py-2 text-xs leading-relaxed text-ink-300">
                {diagnostics.diagnosis}
              </p>
            </div>
          ) : (
            <p className="text-xs leading-relaxed text-ink-500">
              Verifica se a task ainda aparece em active, reserved ou scheduled no Celery.
            </p>
          )}
        </div>

        {actionError && (
          <p className="rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
            {actionError}
          </p>
        )}
      </div>

      {/* Admin actions */}
      {isAdmin && (
        <div className="flex flex-col gap-2 border-t border-ink-700 px-4 py-3">
          {canRetry && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={() => handleAction(() => onRetry(job.id))}
              className="rounded border border-emerald-800 bg-emerald-900/30 px-3 py-1.5 text-xs font-medium text-emerald-300 hover:bg-emerald-900/60 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {actionLoading ? "Aguarde…" : "↺ Reprocessar"}
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={() => handleAction(() => onCancel(job.id))}
              className="rounded border border-red-800 bg-red-900/30 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-900/60 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {actionLoading ? "Aguarde…" : "✕ Cancelar job"}
            </button>
          )}
          {!canRetry && !canCancel && (
            <p className="text-xs text-ink-500">Nenhuma ação disponível para este status.</p>
          )}
        </div>
      )}
    </aside>
  );
}
